import {
  defaultYToolVars,
  deleteYTools,
  ecopetrolYToolVars,
  kocYToolVars,
  restoreYTools
} from "@/models/equipment/y_tool/predicates";
import type { Y_Tools } from "gql/api";
import {
  useDeleteYToolsMutation,
  useInsertYToolMutation,
  useRestoreYToolsMutation,
  useUpdateYToolByPkMutation,
  useYToolsQuery
} from "gql/api";
import { v4 } from "uuid";
import { computed } from "vue";
import { useEquipment } from "@/composables/equipment/baseEquipment";
import type { UseEquipment } from "@/composables/equipment/equipmentTypes";
import { yToolTableColumns } from "./yToolTableColumns";
import useUserInfo from "@/composables/auth/useUserInfo";

export function useYTool(isAdmin: boolean): UseEquipment<Y_Tools> {
  const baseEquipment = useEquipment<Y_Tools>(isAdmin);
  const title = "Y Blocks";
  const user = useUserInfo();

  const getVariables = () => {
    if (user.is_koc_customer) return kocYToolVars;
    if (user.is_ecopetrol_customer) return ecopetrolYToolVars;
    return defaultYToolVars;
  };

  const {
    data,
    executeQuery: refetch,
    stale: isStale
  } = useYToolsQuery({
    variables: getVariables()
  });

  const allRows = computed(() => data.value?.y_tools as Y_Tools[] | undefined);

  const hashOrder: (keyof Y_Tools)[] = [
    "manufacturer",
    "minimum_inches",
    "maximum_inches",
    "length_inches",
    "width_inches",
    "series_model",
    "weight_pounds"
  ];

  const { defineVisibleColumns, defineFilterData } = baseEquipment;

  const { defineUpsert, defineRestore, defineDelete } =
    baseEquipment.defineMutationHandlers(refetch, hashOrder);

  const { executeMutation: executeInsertYTool } = useInsertYToolMutation();
  const { executeMutation: executeUpdateYTool } = useUpdateYToolByPkMutation();
  const { executeMutation: executeDeleteYTool } = useDeleteYToolsMutation();
  const { executeMutation: executeRestoreYTool } = useRestoreYToolsMutation();

  const insertYTool = (
    yTool: Omit<
      Y_Tools,
      | "user"
      | "downhole_scenario_equipment_revisions"
      | "downhole_scenario_equipment_revisions_aggregate"
      | "uphole_scenario_equipment_revisions"
      | "uphole_scenario_equipment_revisions_aggregate"
    >
  ) => executeInsertYTool({ object: yTool });

  const updateYTool = (id: string, yTool: Y_Tools) =>
    executeUpdateYTool({ id, object: yTool });

  const deleteYTool = (ids: string[]) =>
    executeDeleteYTool({ where: deleteYTools(ids) });

  const restoreYTool = (ids: string[]) =>
    executeRestoreYTool({ where: restoreYTools(ids) });

  baseEquipment.defineEmptyRow(() => ({
    __typename: "y_tools",
    id: v4(),
    created_at: "now()",
    updated_at: "now()",
    deleted_at: null,
    manufacturer: null,
    minimum_inches: null,
    maximum_inches: null,
    length_inches: null,
    width_inches: null,
    series_model: null,
    outer_diameter_inches: null,
    teleswivel_stroke_length_inches: null,
    pressure_rating_psi: null,
    temperature_rating_fahrenheit: null,
    handling_sub_connection_inches: null,
    bypass_connection_inches: null,
    material_type: null,
    weight_pounds: null,
    include_koc_timestamp: null,
    only_koc_timestamp: null,
    include_ecopetrol_timestamp: null,
    only_ecopetrol_timestamp: null
  }));

  return {
    searchPlaceholder: "Search Y Blocks...",
    searchToolTip: `Add quotes around search terms for a more exact result. Ex: "Manufacturer"`,
    columns: defineVisibleColumns(yToolTableColumns),
    handleUpsert: defineUpsert(insertYTool, updateYTool),
    handleDelete: defineDelete(deleteYTool),
    handleRestore: defineRestore(restoreYTool),
    totalCount: computed(() => data.value?.y_tools_aggregate.aggregate?.count),
    title,
    allRows,
    isAdmin,
    refetch,
    isStale,
    ...baseEquipment,
    ...defineFilterData({
      columns: yToolTableColumns,
      globalSearchFields: ["manufacturer"],
      allRows,
      title
    })
  };
}
